'use client';

import { useSyncExternalStore } from 'react';

/**
 * One `MediaQueryList` per query for the lifetime of the page.
 *
 * `useSyncExternalStore` calls `getSnapshot` on every render and re-subscribes whenever
 * `subscribe`'s identity changes, so building the list inline means a fresh `MediaQueryList` —
 * and a fresh media-query parse — on every render, and a teardown/re-subscribe on every one too.
 */
type Entry = { matches: () => boolean; subscribe: (onChange: () => void) => () => void };

const entries = new Map<string, Entry>();

/** The list itself is deferred: this runs during render, and render also happens on the server. */
function entryFor(query: string): Entry {
  let entry = entries.get(query);
  if (!entry) {
    let list: MediaQueryList | undefined;
    function listFor() {
      return (list ??= window.matchMedia(query));
    }
    entry = {
      matches: () => listFor().matches,
      subscribe(onChange) {
        const target = listFor();
        target.addEventListener('change', onChange);
        return () => target.removeEventListener('change', onChange);
      },
    };
    entries.set(query, entry);
  }
  return entry;
}

/**
 * SSR-safe `matchMedia`. `serverValue` is what the server and the first client paint assume — pick
 * the branch that costs least to be wrong about, since the other one corrects after hydration.
 */
export function useMediaQuery({
  query,
  serverValue = false,
}: {
  query: string;
  serverValue?: boolean;
}): boolean {
  const entry = entryFor(query);
  return useSyncExternalStore(entry.subscribe, entry.matches, () => serverValue);
}
